import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Path, Resolver, useForm } from "react-hook-form"
import * as Yup from "yup"

import useCurrencyFormatters from "@hooks/useCurrencyFormatters"
import useSlugExtractor from "@hooks/useSlugExtractor"

import applyApiErrorsToForm from "@utils/applyApiErrorsToForm"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import Dialog from "@molecules/Dialog/Dialog"
import Form from "@molecules/Form/Form"
import NumberInput from "@molecules/Form/inputs/NumberInput/NumberInput"
import Select from "@molecules/Select/Select"

import { BILLS_ROUTES } from "@routes/bills"

import styles from "./BillApplyVendorCreditDialog.module.scss"
import { applyVendorCreditRequest, useApplicableVendorCreditsQuery } from "./BillApplyVendorCreditDialog.queries"
import {
    BillApplyVendorCreditDialogProps,
    BillApplyVendorCreditFormProps,
    BillApplyVendorCreditFormValues,
    BillApplyVendorCreditPayload,
} from "./BillApplyVendorCreditDialog.types"

// The server's field errors land on the field of the same name; anything else (a refused bill) reads in the banner.
const BACKEND_TO_FORM_FIELD_MAP: Partial<Record<string, Path<BillApplyVendorCreditFormValues>>> = {
    vendor_credit: "vendor_credit",
    amount: "amount",
}

// Money is compared in whole cents so a typed "0.1" and a served "0.10" agree.
function toCents(value: string) {
    return Math.round(Number(value) * 100)
}

function fromCents(cents: number) {
    return (cents / 100).toFixed(2)
}

function BillApplyVendorCreditForm(props: BillApplyVendorCreditFormProps) {
    const { billID, amountDue, vendorID, onApplied, onCancel } = props

    const [endpoint] = useSlugExtractor([BILLS_ROUTES.APPLY_VENDOR_CREDIT], billID)
    const { formatCurrency } = useCurrencyFormatters()
    const {
        data: vendorCredits,
        isLoading: isLoadingVendorCredits,
        isError: isVendorCreditsError,
    } = useApplicableVendorCreditsQuery(vendorID)

    const [nonFieldError, setNonFieldError] = useState<string | null>(null)
    // Applying reloads the page; the button stays busy until then so a second click cannot apply the credit twice.
    const [hasApplied, setHasApplied] = useState(false)

    // The most a credit can give this bill: what it has left, never more than the bill still owes.
    const getCeilingCents = useCallback(
        (vendorCreditID: string | undefined) => {
            const vendorCredit = vendorCredits?.find((candidate) => candidate.id === vendorCreditID)
            return vendorCredit ? Math.min(toCents(vendorCredit.amount_remaining), toCents(amountDue)) : null
        },
        [vendorCredits, amountDue],
    )

    const vendorCreditOptions = useMemo(
        () =>
            (vendorCredits ?? []).map((vendorCredit) => ({
                label: vendorCredit.custom_id || vendorCredit.id,
                value: vendorCredit.id,
                subtext: formatCurrency(vendorCredit.amount_remaining),
                supportText: `Issued ${dayjs(vendorCredit.date_issued).format("MMM D, YYYY")}`,
            })),
        [vendorCredits, formatCurrency],
    )

    const formSchema: Yup.ObjectSchema<BillApplyVendorCreditFormValues> = useMemo(
        () =>
            Yup.object().shape({
                vendor_credit: Yup.string().required("Please select a vendor credit."),
                amount: Yup.string()
                    .required("Please enter an amount.")
                    .matches(/^\d*(\.\d{1,2})?$/, "Please enter an amount with at most two decimal places.")
                    .test(
                        "minimum",
                        `The amount must be at least ${formatCurrency("0.01")}.`,
                        (value) => toCents(value) >= 1,
                    )
                    .test("ceiling", "", function (value) {
                        const ceilingCents = getCeilingCents(
                            (this.parent as BillApplyVendorCreditFormValues).vendor_credit,
                        )
                        if (ceilingCents !== null && toCents(value) > ceilingCents) {
                            return this.createError({
                                message: `The amount cannot exceed ${formatCurrency(fromCents(ceilingCents))}.`,
                            })
                        } else {
                            return true
                        }
                    }),
            }),
        [getCeilingCents, formatCurrency],
    )

    const form = useForm<BillApplyVendorCreditFormValues>({
        resolver: yupResolver(formSchema) as Resolver<BillApplyVendorCreditFormValues>,
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            vendor_credit: "",
            amount: "",
        },
    })

    const applyVendorCreditMutation = useMutation({
        mutationFn: (payload: BillApplyVendorCreditPayload) => applyVendorCreditRequest(endpoint, payload),
        onSuccess: () => {
            setHasApplied(true)
            onApplied()
        },
        onError: (error: ApiErrorResponse) => {
            applyApiErrorsToForm(
                form,
                error,
                BACKEND_TO_FORM_FIELD_MAP,
                "The vendor credit could not be applied",
                setNonFieldError,
            )
        },
    })

    const isApplying = applyVendorCreditMutation.isPending || hasApplied

    const handleSubmit = (values: BillApplyVendorCreditFormValues) => {
        if (!isApplying) {
            setNonFieldError(null)
            applyVendorCreditMutation.mutate({
                vendor_credit: values.vendor_credit,
                amount: fromCents(toCents(values.amount)),
            })
        }
    }

    const selectedVendorCreditID = form.watch("vendor_credit")
    const selectedCeilingCents = getCeilingCents(selectedVendorCreditID)

    // Choosing a credit starts its amount at the most it can give.
    useEffect(() => {
        const subscription = form.watch((values, { name }) => {
            const ceilingCents = getCeilingCents(values.vendor_credit)
            if (name === "vendor_credit" && ceilingCents !== null) {
                form.setValue("amount", fromCents(ceilingCents), { shouldValidate: form.formState.isSubmitted })
            }
        })
        return () => subscription.unsubscribe()
    }, [form, getCeilingCents])

    // A vendor with a single credit has it chosen already, with its full applicable amount.
    useEffect(() => {
        if (vendorCredits?.length === 1) {
            form.setValue("vendor_credit", vendorCredits[0].id)
        }
    }, [form, vendorCredits])

    return (
        <Form form={form} onSubmit={form.handleSubmit(handleSubmit) as () => void}>
            <Dialog.Header.Root>
                <Dialog.Header.Title title="Apply Vendor Credit" />
                <Dialog.Header.CloseButton />
            </Dialog.Header.Root>
            <Dialog.Body>
                <div className={styles.body}>
                    <Select.Root>
                        <Select.Input<BillApplyVendorCreditFormValues>
                            name="vendor_credit"
                            size="md"
                            placeholder="Choose vendor credit..."
                            label="Vendor credit"
                            options={vendorCreditOptions}
                        />
                        <Select.Viewport>
                            <Select.List title="Vendor credit">
                                <Select.ListSearch
                                    placeholder="Search by number..."
                                    isLoading={isLoadingVendorCredits}
                                />
                                <Select.ListGroup>
                                    {isLoadingVendorCredits ? (
                                        <>
                                            <Select.ListItem
                                                variant="default"
                                                item={{ label: "", value: "loading-1" }}
                                                isLoading={true}
                                            />
                                            <Select.ListItem
                                                variant="default"
                                                item={{ label: "", value: "loading-2" }}
                                                isLoading={true}
                                            />
                                        </>
                                    ) : (
                                        vendorCreditOptions.map((option) => (
                                            <Select.ListItem
                                                key={option.value}
                                                variant="default"
                                                item={option}
                                                hasSupportText={true}
                                                supportText={option.supportText}
                                                hasSubtext={true}
                                                subtext={option.subtext}
                                            />
                                        ))
                                    )}
                                </Select.ListGroup>
                            </Select.List>
                        </Select.Viewport>
                    </Select.Root>
                    <NumberInput<BillApplyVendorCreditFormValues>
                        name="amount"
                        size="md"
                        label="Amount"
                        min={0.01}
                        step={0.01}
                        isFullWidth={true}
                        inputHelpText={
                            selectedCeilingCents !== null
                                ? `Up to ${formatCurrency(fromCents(selectedCeilingCents))}`
                                : undefined
                        }
                    />
                    {isVendorCreditsError && (
                        <AlertBanner type="card" status="error" message="The vendor credits could not be loaded" />
                    )}
                    {nonFieldError && <AlertBanner type="card" status="error" message={nonFieldError} />}
                </div>
            </Dialog.Body>
            <Dialog.Footer.Root>
                <Button variant="ghost" size="lg" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button variant="solid" size="lg" type="submit" isProcessing={isApplying} isDisabled={isApplying}>
                    Apply Credit
                </Button>
            </Dialog.Footer.Root>
        </Form>
    )
}

export default function BillApplyVendorCreditDialog(props: BillApplyVendorCreditDialogProps) {
    const { isOpen, onOpenChange, billID, amountDue, vendorID, onApplied } = props

    // Store onOpenChange in a ref so the callback identity stays stable across the legacy card's re-renders.
    // The Popover's useEffect depends on onOpenChange, and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // The form lives inside the content, which mounts on every opening, so each opening fetches the credits and starts
    // from fresh defaults.
    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <BillApplyVendorCreditForm
                    billID={billID}
                    amountDue={amountDue}
                    vendorID={vendorID}
                    onApplied={onApplied}
                    onCancel={() => stableOnOpenChange(false)}
                />
            </Dialog.Content>
        </Dialog.Root>
    )
}
